/**
 *  User experience & adoption
 *  Build quality & technical debt
 *  Automation, AI & efficiency
 *  Data & integrations
**/

export const QUESTIONS = [
  {
    group: "User experience & Adoption",
    shortLabel: "Company adoption",
    polarity: "good-practice",
    weight: 1,
    text: "What portion of your company uses Airtable in some capacity?",
    context: "Every additional team using Airtable increases the ROI of the platform and reduces fragmentation across the organisation.",
    answerAdvice: {
      "All": "<h4>Full adoption across every team.</h4><p>Airtable is your operational platform. Great work bringing everyone along.</p>",
      "Most": "<h4>Good progress towards adoption.</h4><p>Identify which teams aren't on Airtable yet and run a lightweight pilot. A workshop and a prototype can go a long way.</p>",
      "Some": "<h4>Room for growth.</h4><p>Identify 1-2 more teams whose workflows could move into Airtable and run a pilot. The payoff compounds as more teams join.</p>",
      "None": "<h4>No adoption yet.</h4><p>If no teams have adopted Airtable yet, find the use case with the strongest pull and start there. Wide adoption needs a visible internal success story first.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Spokes -->
  <line x1="140" y1="74" x2="44" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="236" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="44" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="236" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <!-- Central hub -->
  <rect x="106" y="40" width="68" height="68" rx="12" fill="#261299"/>
  <rect x="118" y="52" width="12" height="12" rx="3" fill="white" opacity="0.9"/>
  <rect x="134" y="52" width="12" height="12" rx="3" fill="white" opacity="0.6"/>
  <rect x="150" y="52" width="10" height="12" rx="3" fill="white" opacity="0.35"/>
  <rect x="118" y="68" width="12" height="10" rx="3" fill="white" opacity="0.6"/>
  <rect x="134" y="68" width="12" height="10" rx="3" fill="white" opacity="0.9"/>
  <rect x="118" y="82" width="12" height="10" rx="3" fill="white" opacity="0.35"/>
  <rect x="134" y="82" width="12" height="10" rx="3" fill="white" opacity="0.5"/>
  <!-- Team nodes: two person icons per corner -->
  <circle cx="32" cy="20" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <circle cx="46" cy="20" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <path d="M22 34 Q32 28 42 34" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M36 34 Q46 28 56 34" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <circle cx="224" cy="20" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <circle cx="238" cy="20" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <path d="M214 34 Q224 28 234 34" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M228 34 Q238 28 248 34" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <circle cx="32" cy="112" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <circle cx="46" cy="112" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <path d="M22 126 Q32 120 42 126" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M36 126 Q46 120 56 126" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <circle cx="224" cy="112" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <circle cx="238" cy="112" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <path d="M214 126 Q224 120 234 126" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M228 126 Q238 120 248 126" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
</svg>`,
  },
  {
    group: "User experience & Adoption",
    shortLabel: "Interface-only access",
    polarity: "good-practice",
    weight: 2,
    text: "What portion of your end users are working in Interfaces?",
    context: "Interfaces create a true app layer with better workflows, enforced permissions and less noise.",
    answerAdvice: {
      "All": "<h4>You're deploying real software to your users.</h4><p>A proper app layer for your users provides a great foundation for data integrity and a professional user experience.</p>",
      "Most": "<h4>Good progress.</h4><p>Find the workflows still running in raw data and build interfaces for them. Users will grow to prefer a more focused user experience faster than you'd expect.</p>",
      "Some": "<h4>Room for growth.</h4><p>Moving users out of raw tables is the highest-leverage change you can make. Start with your most-used workflows and go from there. Users will grow to prefer a more focused experience quicker than you'd expect.</p>",
      "None": "<h4>No interfaces yet.</h4><p>Everyone started here in the data layer, and it's worth remembering that thousands of companies rolled out Airtable before interfaces existed. The key is to start small and lead by example. Interfaces are quick to spin up and very powerful once they're adopted as standard.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Left: raw data grid (bad) -->
  <rect x="16" y="34" width="90" height="72" rx="6" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <rect x="16" y="34" width="90" height="16" rx="6" fill="#f0effe"/>
  <line x1="46" y1="34" x2="46" y2="106" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="76" y1="34" x2="76" y2="106" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="16" y1="68" x2="106" y2="68" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="16" y1="88" x2="106" y2="88" stroke="#e8e6f8" stroke-width="1"/>
  <circle cx="61" cy="24" r="9" fill="#fee2e2"/>
  <path d="M57 20 L65 28 M65 20 L57 28" stroke="#d1242f" stroke-width="2" stroke-linecap="round"/>
  <!-- Arrow -->
  <path d="M112 70 L150 70" stroke="#261299" stroke-width="2.5" stroke-linecap="round"/>
  <path d="M145 65 L151 70 L145 75" fill="none" stroke="#261299" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Right: clean interface (good) -->
  <rect x="156" y="22" width="108" height="104" rx="8" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="156" y="22" width="108" height="24" rx="8" fill="#261299"/>
  <rect x="168" y="56" width="84" height="9" rx="4" fill="#eeedfc"/>
  <rect x="168" y="71" width="84" height="9" rx="4" fill="#eeedfc"/>
  <rect x="168" y="86" width="58" height="9" rx="4" fill="#eeedfc"/>
  <rect x="168" y="103" width="38" height="11" rx="5" fill="#261299"/>
  <circle cx="210" cy="12" r="9" fill="#dcfce7"/>
  <path d="M206 12 L209 15 L215 9" stroke="#0f8548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  },
  {
    group: "User experience & Adoption",
    shortLabel: "User documentation",
    polarity: "good-practice",
    weight: 1,
    text: "What portion of your Airtable apps have supporting documentation?",
    context: "Every application has company-specific terminology, processes, automations hiding under the hood.",
    answerAdvice: {
      "All": "<h4>You're allowing your users to self-serve</h4><p>Accessible documentation prevents builders from being a single point of failure, and has probably saved you a lot of noise in your inbox.</p><p><b>Top tip:</b> Signpost your docs within interfaces to meet your users where they are.</p>",
      "Most": "<h4>You've made great progress in supporting your users.</h4><p>Documentation can feel endless. Try enlisting the help of power users to take ownership of some of the documentation.</p>",
      "Some": "<h4>You've kept a few support questions out of your inbox.</h4><p>Aim to get to 80% coverage of your key features. Try enlisting the help of power users to take ownership of some of the documentation.</p>",
      "None": "<h4>You haven't documented your apps yet.</h4><p>Start with one doc. A brief Loom walkthrough is a great start. Without it, you're a permanent dependency for anyone who needs help.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="116" y="34" width="76" height="96" rx="8" fill="#e0dff0"/>
  <rect x="108" y="26" width="76" height="96" rx="8" fill="white" stroke="#261299" stroke-width="2"/>
  <path d="M164 26 L164 56 L157 49 L150 56 L150 26 Z" fill="#6D1E6F"/>
  <rect x="120" y="42" width="20" height="7" rx="3" fill="#261299"/>
  <rect x="120" y="58" width="52" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="69" width="46" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="80" width="52" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="91" width="34" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="106" width="48" height="5" rx="2" fill="#d0cdfc"/>
</svg>`,
  },
  {
    group: "Build quality & technical debt",
    shortLabel: "Naming hygiene",
    polarity: "good-practice",
    weight: 1,
    text: "Do your bases have descriptive names for tables, fields and automations?",
    context: "Descriptive names are a quick win, the cheapest sign that a system is maintainable.",
    answerAdvice: {
      "All": "<h4>You've completely removed guesswork for the next builder or AI reading your application.</h4><p>Clean naming makes everything easier to debug, hand over, and maintain.</p>",
      "Most": "<h4>Tidy up the stragglers.</h4><p>Generic names like 'Automation 3 copy' become landmines when something breaks unexpectedly.</p>",
      "Some": "<h4>Get a naming convention in place</h4><p>Set a naming convention now and apply it going forward.</p><p>Start with automations — they're the hardest to debug without clear names.</p>",
      "None": "<h4>Rename your tables, fields, and automations now.</h4><p>Descriptive names are the cheapest way to make a system maintainable, and the biggest nightmare when something breaks unexpectedly. You'll thank yourself later.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="40" y="14" width="200" height="120" rx="10" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <!-- Row 1 — good (named table) -->
  <rect x="40" y="14" width="200" height="38" rx="10" fill="#f8f7ff"/>
  <circle cx="66" cy="33" r="10" fill="#dcfce7"/>
  <path d="M61 33 L64 36 L71 30" stroke="#0f8548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <rect x="82" y="26" width="80" height="7" rx="3" fill="#261299" opacity="0.8"/>
  <rect x="82" y="37" width="56" height="5" rx="2" fill="#d0cdfc"/>
  <!-- Row 2 — good (named field) -->
  <circle cx="66" cy="71" r="10" fill="#dcfce7"/>
  <path d="M61 71 L64 74 L71 68" stroke="#0f8548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <rect x="82" y="64" width="96" height="7" rx="3" fill="#261299" opacity="0.8"/>
  <rect x="82" y="75" width="66" height="5" rx="2" fill="#d0cdfc"/>
  <!-- Row 3 — bad (unnamed automation) -->
  <circle cx="66" cy="111" r="10" fill="#fee2e2"/>
  <path d="M61 107 L71 115 M71 107 L61 115" stroke="#d1242f" stroke-width="2" stroke-linecap="round"/>
  <rect x="82" y="104" width="116" height="7" rx="3" fill="#fecdca"/>
  <rect x="82" y="115" width="80" height="5" rx="2" fill="#fecdca" opacity="0.6"/>
</svg>`,
  },
  {
    group: "Build quality & technical debt",
    shortLabel: "Sandbox usage",
    polarity: "good-practice",
    weight: 1.5,
    text: "Do you use Sandbox mode to test updates before publishing them?",
    context: "Sandbox mode lets you test changes on a copy of your base before pushing them live.",
    answerAdvice: {
      "Always": "<h4>Using the full toolkit to your advantage.</h4><p>Excellent release discipline. Sandbox use means production changes have been tested — far fewer surprises.</p>",
      "Mostly": "<h4>That's a solid habit for your dev process.</h4><p>Work out when you skip it and try to make Sandbox the default for everything beyond a trivial label change.</p>",
      "Sometimes": "<h4>Be careful experimenting in your production environment.</h4><p>Every live change is an experiment on real data. Get into the habit of using Sandbox for anything structural that could have adverse effects.</p>",
      "Never": "<h4>Try sandbox mode for your next structural update.</h4><p>One broken live automation costs more time than every sandbox test combined, and restoring from backup is not an easy process sadly.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="16" y="38" width="104" height="72" rx="10" fill="#faf9ff" stroke="#6D1E6F" stroke-width="2" stroke-dasharray="6 3"/>
  <path d="M126 74 L154 74" stroke="#261299" stroke-width="2.5" stroke-linecap="round"/>
  <path d="M149 69 L155 74 L149 79" fill="none" stroke="#261299" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  <rect x="160" y="38" width="104" height="72" rx="10" fill="white" stroke="#261299" stroke-width="2"/>
</svg>`,
  },
  {
    group: "Automation, AI & efficiency",
    shortLabel: "Automation run limits",
    polarity: "symptom",
    weight: 1.5,
    text: "Has your workspace run out of automation runs on more than one occasion?",
    context: "Every Airtable plan has a monthly cap on automation runs, be sure to plan ahead.",
    answerAdvice: {
      "Yes": "<h4>It happens to the best of us.</h4><p>Audit your automations for unnecessary triggers. Batching, conditional logic, and de-duplication can cut run usage dramatically. For workflows that don't need to run immediately, use a scheduled time (every x hours) to give yourself a predictable run count each month.</p>",
      "No": "<h4>That's impressive automation planning.</h4><p>Your automations are running within capacity/no wasted runs burning through your monthly limit.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <path d="M68 118 A 72 72 0 0 1 212 118" stroke="#e8e6f8" stroke-width="18" fill="none" stroke-linecap="round"/>
  <path d="M68 118 A 72 72 0 0 1 140 46" stroke="#0f8548" stroke-width="18" fill="none" stroke-linecap="round" opacity="0.65"/>
  <path d="M140 46 A 72 72 0 0 1 188 62" stroke="#c27803" stroke-width="18" fill="none" stroke-linecap="round" opacity="0.85"/>
  <path d="M188 62 A 72 72 0 0 1 212 118" stroke="#d1242f" stroke-width="18" fill="none" stroke-linecap="round"/>
  <line x1="140" y1="118" x2="198" y2="74" stroke="#261299" stroke-width="4" stroke-linecap="round"/>
  <circle cx="140" cy="118" r="8" fill="#261299"/>
  <circle cx="140" cy="118" r="3.5" fill="white"/>
</svg>`,
  },
  {
    group: "Build quality & technical debt",
    shortLabel: "View/page cloning",
    polarity: "symptom-graded",
    weight: 1,
    text: "Do you have views or pages named for specific individuals?",
    context: "CRM (Jenny), CRM (Mike), CRM (David) etc. Cloning a view or page per person seems quick, but every design change is slower to roll out.",
    answerAdvice: {
      "Yes": "<h4>Replace per-person cloned views with filtered interface pages.</h4><p>Every clone adds maintenance debt when the design changes, and for every person who joins/leaves.</p>",
      "Some": "<h4>Make a plan to migrate those copies to filtered interface pages.</h4><p>When you catch yourself cloning, pause and ask if a filtered view or permission layer would handle it. It almost always does, and it scales with zero technical effort.</p>",
      "Never": "<h4>You've removed the need for copy/paste development.</h4><p>Filtered permission-based interfaces scale; per-person clones just don't.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="116" y="44" width="84" height="92" rx="6" fill="white" stroke="#e0dff0" stroke-width="1.5"/>
  <rect x="116" y="44" width="84" height="18" rx="6" fill="#f0effe"/>
  <rect x="126" y="70" width="54" height="5" rx="2" fill="#e8e6f8"/>
  <rect x="126" y="81" width="46" height="5" rx="2" fill="#e8e6f8"/>
  <rect x="102" y="32" width="84" height="92" rx="6" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <rect x="102" y="32" width="84" height="18" rx="6" fill="#eeedfc"/>
  <rect x="112" y="58" width="54" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="112" y="69" width="46" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="86" y="18" width="84" height="92" rx="6" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="86" y="18" width="84" height="20" rx="6" fill="#261299"/>
  <rect x="96" y="46" width="54" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="96" y="57" width="46" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="96" y="88" width="64" height="16" rx="8" fill="#eeedfc" stroke="#261299" stroke-width="1.5"/>
  <g transform="translate(5, 0)">
  <rect x="200" y="18" width="48" height="42" rx="8" fill="#fee2e2"/>
  <rect x="208" y="24" width="22" height="22" rx="4" fill="white" stroke="#d1242f" stroke-width="1.5"/>
  <rect x="216" y="32" width="22" height="22" rx="4" fill="white" stroke="#d1242f" stroke-width="1.5"/>
  </g>
</svg>`,
  },
  {
    group: "Data & integrations",
    shortLabel: "Data syncing",
    polarity: "architecture",
    weight: 1.5,
    text: "Are you using synced data or published datasets to share data between bases?",
    context: "If the same master data (clients, products, team members) lives in multiple bases without syncing, it will gradually drift out of sync. Synced tables keep one source of truth.",
    answerAdvice: {
      "Yes": "<h4>You're managing a single source of truth.</h4><p>Synced tables keep one source of truth across your bases, that's the mature pattern for multi-base setups. Your organisation's already reaping the benefits.</p>",
      "No": "<h4>Start working on your master datasets.</h4><p>Set up synced tables or published datasets between your bases. Duplicated master data always drifts out of sync eventually. Sharing data means building new use cases on solid ground, with everyone working from the same information.</p>",
      "Not applicable": "",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="16" y="34" width="96" height="80" rx="10" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="16" y="34" width="96" height="26" rx="10" fill="#261299"/>
  <rect x="28" y="70" width="64" height="7" rx="3" fill="#eeedfc"/>
  <rect x="28" y="83" width="54" height="7" rx="3" fill="#eeedfc"/>
  <rect x="28" y="96" width="60" height="7" rx="3" fill="#eeedfc"/>
  <rect x="168" y="34" width="96" height="80" rx="10" fill="white" stroke="#6D1E6F" stroke-width="2"/>
  <rect x="168" y="34" width="96" height="26" rx="10" fill="#6D1E6F"/>
  <rect x="180" y="70" width="64" height="7" rx="3" fill="#f5eef5"/>
  <rect x="180" y="83" width="54" height="7" rx="3" fill="#f5eef5"/>
  <rect x="180" y="96" width="60" height="7" rx="3" fill="#f5eef5"/>
  <path d="M116 64 Q140 58 164 64" stroke="#261299" stroke-width="2.5" fill="none" stroke-linecap="round"/>
  <path d="M159 59 L165 64 L159 69" fill="none" stroke="#261299" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M164 98 Q140 104 116 98" stroke="#6D1E6F" stroke-width="2.5" fill="none" stroke-linecap="round"/>
  <path d="M121 93 L115 98 L121 103" fill="none" stroke="#6D1E6F" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  },
  {
    group: "Data & integrations",
    shortLabel: "External data sync",
    polarity: "architecture",
    weight: 1,
    text: "Do you have any integrations to sync data in/out of Airtable?",
    context: "Airtable becomes most valuable when it talks to the tools around it — CRM, email, calendar, accounting. Without integrations, data lives in silos and must be re-entered by hand.",
    answerAdvice: {
      "Yes": "<h4>You're connecting islands into a unified system.</h4><p>Airtable connected to your surrounding tools is Airtable as a system of record, instead of another isolated island.</p>",
      "No": "<h4>Start working on your integration strategy.</h4><p>Connect Airtable to your surrounding tools. Even a simple Zapier sync eliminates manual re-keying and starts making Airtable the hub.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <line x1="140" y1="74" x2="56" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="224" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="56" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="224" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <rect x="108" y="42" width="64" height="64" rx="12" fill="#261299"/>
  <rect x="120" y="54" width="12" height="12" rx="3" fill="white" opacity="0.9"/>
  <rect x="136" y="54" width="12" height="12" rx="3" fill="white" opacity="0.6"/>
  <rect x="152" y="54" width="8" height="12" rx="3" fill="white" opacity="0.35"/>
  <rect x="120" y="70" width="12" height="10" rx="3" fill="white" opacity="0.6"/>
  <rect x="136" y="70" width="12" height="10" rx="3" fill="white" opacity="0.9"/>
  <rect x="152" y="70" width="8" height="10" rx="3" fill="white" opacity="0.35"/>
  <rect x="120" y="84" width="12" height="8" rx="3" fill="white" opacity="0.35"/>
  <rect x="136" y="84" width="12" height="8" rx="3" fill="white" opacity="0.5"/>
  <!-- Top-left: email -->
  <rect x="28" y="10" width="40" height="30" rx="6" fill="white" stroke="#261299" stroke-width="1.5"/>
  <path d="M28 16 L48 27 L68 16" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <!-- Top-right: calendar -->
  <rect x="212" y="10" width="40" height="30" rx="6" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <rect x="218" y="16" width="28" height="18" rx="3" fill="#f0effe"/>
  <rect x="218" y="16" width="28" height="8" rx="3" fill="#6D1E6F"/>
  <circle cx="226" cy="31" r="2" fill="#6D1E6F" opacity="0.5"/>
  <circle cx="233" cy="31" r="2" fill="#6D1E6F" opacity="0.5"/>
  <!-- Bottom-left: people -->
  <rect x="28" y="108" width="40" height="30" rx="6" fill="white" stroke="#261299" stroke-width="1.5"/>
  <circle cx="48" cy="119" r="7" fill="#eeedfc" stroke="#261299" stroke-width="1"/>
  <path d="M36 136 Q48 130 60 136" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <!-- Bottom-right: lightning/zap -->
  <rect x="212" y="108" width="40" height="30" rx="6" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <path d="M235 114 L227 126 L233 126 L224 138 L240 124 L234 124 Z" fill="#6D1E6F"/>
</svg>`,
  },
  {
    group: "Automation, AI & efficiency",
    shortLabel: "Automation coverage",
    polarity: "good-practice",
    weight: 1.5,
    text: "What portion of your teams have successfully automated at least one manual task?",
    context: "Automations are the difference between a spreadsheet and a system. If most workflows still require manual steps, you're leaving efficiency on the table.",
    answerAdvice: {
      "All": "<h4>You're fully automated.</h4><p>You're getting the most out of Airtable as an operational system, not just a database.</p>",
      "Most": "<h4>You're shedding repetitive tasks.</h4><p>Map the remaining manual processes and add automations. The effort per automation drops once you're in the habit.</p>",
      "Some": "<h4>Start automating your repetitive tasks.</h4><p>Map your repetitive workflows and add automations. Even a simple trigger-action rule eliminates manual steps and frees up your team.</p>",
      "None": "<h4>Begin your automation journey.</h4><p>Automations are what turn a spreadsheet into a system. Start with your most repetitive workflow — notify, create, update.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Step 1: Record -->
  <rect x="10" y="44" width="52" height="60" rx="8" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="10" y="44" width="52" height="18" rx="8" fill="#261299"/>
  <rect x="18" y="70" width="36" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="18" y="81" width="28" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="18" y="92" width="32" height="5" rx="2" fill="#d0cdfc"/>
  <!-- Arrow 1 -->
  <path d="M66 74 L80 74" stroke="#261299" stroke-width="2" stroke-linecap="round"/>
  <path d="M77 71 L81 74 L77 77" fill="none" stroke="#261299" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Step 2: Trigger (lightning in circle) -->
  <circle cx="101" cy="74" r="22" fill="#261299"/>
  <path d="M105 58 L96 76 L103 76 L97 90 L114 72 L107 72 Z" fill="white" opacity="0.9"/>
  <!-- Arrow 2 -->
  <path d="M125 74 L139 74" stroke="#261299" stroke-width="2" stroke-linecap="round"/>
  <path d="M136 71 L140 74 L136 77" fill="none" stroke="#261299" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Step 3: Action (envelope) -->
  <rect x="142" y="50" width="52" height="48" rx="8" fill="white" stroke="#6D1E6F" stroke-width="2"/>
  <rect x="142" y="50" width="52" height="18" rx="8" fill="#6D1E6F" opacity="0.85"/>
  <rect x="150" y="78" width="36" height="5" rx="2" fill="#f5eef5"/>
  <rect x="150" y="88" width="24" height="5" rx="2" fill="#f5eef5"/>
  <!-- Arrow 3 -->
  <path d="M197 74 L211 74" stroke="#261299" stroke-width="2" stroke-linecap="round"/>
  <path d="M208 71 L212 74 L208 77" fill="none" stroke="#261299" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Step 4: Done checkmark -->
  <circle cx="232" cy="74" r="22" fill="#dcfce7" stroke="#0f8548" stroke-width="2"/>
  <path d="M222 74 L229 81 L243 67" stroke="#0f8548" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  },
  {
    group: "Automation, AI & efficiency",
    shortLabel: "AI usage",
    polarity: "good-practice",
    weight: 0.5,
    text: "How much does your team use AI within Airtable?",
    context: "Airtable's AI features — AI fields, AI in automations can summarise records, classify data and attachments and draft communications automatically.",
    answerAdvice: {
      "A lot": "<h4>You're ahead of the curve.</h4><p>AI within Airtable compounds your efficiency gains. Users will be looking for new opportunities to leverage AI.</p>",
      "Sometimes": "<h4>Keep experimenting.</h4><p>AI fields for summarisation and classification are where the consistent time savings are.</p>",
      "A little": "<h4>Explore AI fields.</h4><p>Summarising or tagging records — they're often a quick drop-in that saves real time at scale.</p>",
      "Never": "<h4>Try an AI field.</h4><p>Start with one table — summarising notes or classifying records are low-effort, high-value starting points.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Record card -->
  <rect x="20" y="18" width="240" height="112" rx="10" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="20" y="18" width="240" height="26" rx="10" fill="#261299"/>
  <!-- Header label bar -->
  <rect x="32" y="26" width="72" height="8" rx="4" fill="white" opacity="0.35"/>
  <!-- AI badge in header -->
  <rect x="194" y="24" width="54" height="16" rx="8" fill="#6D1E6F"/>
  <rect x="202" y="29" width="38" height="6" rx="3" fill="white" opacity="0.7"/>
  <!-- Normal field rows -->
  <rect x="32" y="52" width="56" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="32" y="64" width="196" height="8" rx="3" fill="#261299" opacity="0.12"/>
  <rect x="32" y="78" width="56" height="6" rx="3" fill="#d0cdfc"/>
  <!-- AI-generated field (dashed border, tinted) -->
  <rect x="32" y="90" width="196" height="30" rx="6" fill="#f0effe" stroke="#6D1E6F" stroke-width="1.5" stroke-dasharray="5 2.5"/>
  <rect x="40" y="98" width="110" height="6" rx="3" fill="#6D1E6F" opacity="0.45"/>
  <rect x="40" y="108" width="78" height="6" rx="3" fill="#6D1E6F" opacity="0.28"/>
  <!-- Sparkles -->
  <path d="M242 86 L244 80 L246 86 L252 88 L246 90 L244 96 L242 90 L236 88 Z" fill="#6D1E6F"/>
  <path d="M256 98 L257.5 93 L259 98 L264 99.5 L259 101 L257.5 106 L256 101 L251 99.5 Z" fill="#6D1E6F" opacity="0.55"/>
  <path d="M248 70 L249 67 L250 70 L253 71 L250 72 L249 75 L248 72 L245 71 Z" fill="#261299" opacity="0.45"/>
</svg>`,
  },
  
  {
    group: "Build quality & technical debt",
    shortLabel: "Repeated columns",
    polarity: "symptom",
    weight: 1,
    text: "Do you have any repeated columns in tables?",
    context: "Month 1, Month 2, Month 3 or Person 1, Person 2, Person 3. Repeated columns come from treating a database like a spreadsheet.",
    answerAdvice: {
      "Yes": "<h4>Convert those repeated column groups.</h4><p>Convert those repeated column groups into a child table with one row per period/person, linked back to the parent. It enables proper filtering, rollups, and future growth without breaking the schema. See: 'database normalisation'.</p>",
      "No": "<h4>Great schema discipline.</h4><p>Avoiding repeated columns shows youre understanding how to shape relational data.</p>",
    },
    image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Table container -->
  <rect x="14" y="22" width="252" height="104" rx="8" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <!-- Header row (repeated columns highlighted bad) -->
  <rect x="14" y="22" width="252" height="26" rx="8" fill="#fee2e2"/>
  <!-- Name column header -->
  <rect x="22" y="29" width="36" height="10" rx="3" fill="#fecdca" opacity="0.8"/>
  <!-- Month columns (repeated, highlighted red) -->
  <rect x="70" y="29" width="36" height="10" rx="3" fill="#d1242f" opacity="0.55"/>
  <rect x="116" y="29" width="36" height="10" rx="3" fill="#d1242f" opacity="0.55"/>
  <rect x="162" y="29" width="36" height="10" rx="3" fill="#d1242f" opacity="0.55"/>
  <!-- Last column fading with "..." -->
  <rect x="208" y="29" width="28" height="10" rx="3" fill="#d1242f" opacity="0.25"/>
  <!-- Column dividers -->
  <line x1="62" y1="22" x2="62" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="108" y1="22" x2="108" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="154" y1="22" x2="154" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="200" y1="22" x2="200" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <!-- Row dividers -->
  <line x1="14" y1="48" x2="266" y2="48" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="14" y1="66" x2="266" y2="66" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="14" y1="84" x2="266" y2="84" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="14" y1="102" x2="266" y2="102" stroke="#e8e6f8" stroke-width="1"/>
  <!-- Data cells -->
  <rect x="22" y="54" width="28" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="54" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="116" y="54" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="162" y="54" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="208" y="54" width="20" height="6" rx="3" fill="#eeedfc"/>
  <rect x="22" y="72" width="32" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="72" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="116" y="72" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="162" y="72" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="208" y="72" width="20" height="6" rx="3" fill="#eeedfc"/>
  <rect x="22" y="90" width="24" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="90" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="116" y="90" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="162" y="90" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="208" y="90" width="20" height="6" rx="3" fill="#eeedfc"/>
  <rect x="22" y="108" width="30" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="108" width="24" height="6" rx="3" fill="#eeedfc"/>
  <!-- Warning badge -->
  <g transform="translate(-20, -10)">
  <circle cx="270" cy="13" r="9" fill="#fee2e2"/>
  <path d="M266 9 L274 17 M274 9 L266 17" stroke="#d1242f" stroke-width="2" stroke-linecap="round"/>
  </g>
</svg>`,
  },
];
